import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  getBumpOptions,
  transformBumpOptionsResponse,
  BumpOptionModel,
} from '@marketplace-web/vas/vas-data'

import { PreCheckoutModal } from '../../../../constants/bumps'
import useBumpCheckoutContext from '../../../../hooks/useBumpCheckoutContext'

const useBumpOptions = (activeModal: PreCheckoutModal) => {
  const { selectedItems } = useBumpCheckoutContext()

  const [selectedBumpOption, setSelectedBumpOption] = useState<BumpOptionModel | null>(null)
  const [isOptionsDataStale, setIsOptionsDataStale] = useState(false)

  const {
    fetch: fetchBumpOptions,
    transformedData: bumpOptions,
    isLoading: isBumpOptionsLoading,
    error: bumpOptionsError,
  } = useFetch(getBumpOptions, transformBumpOptionsResponse)

  const updateSelectedBumpOption = useCallback(
    (optionsData: BumpOptionModel) => {
      setSelectedBumpOption(optionsData)
    },
    [setSelectedBumpOption],
  )

  useEffect(() => {
    setIsOptionsDataStale(true)
  }, [selectedItems])

  const handleFetchBumpOptions = useCallback(async () => {
    const response = await fetchBumpOptions({ itemIds: selectedItems.map(item => item.id) })

    if (!response.transformedData) return

    const defaultOption =
      response.transformedData.options.find(option => option.isDefault) ||
      response.transformedData.options[0]!

    setSelectedBumpOption(defaultOption)
    setIsOptionsDataStale(false)
  }, [fetchBumpOptions, selectedItems])

  useEffect(() => {
    if (activeModal === PreCheckoutModal.None) return
    if (activeModal === PreCheckoutModal.GenericErrorModal) return
    if (!isOptionsDataStale) return
    if (!selectedItems.length) return

    handleFetchBumpOptions()
  }, [handleFetchBumpOptions, selectedItems, isOptionsDataStale, activeModal])

  return {
    bumpOptions,
    isBumpOptionsLoading,
    bumpOptionsError,
    selectedBumpOption,
    updateSelectedBumpOption,
  }
}

export default useBumpOptions
